import type { Message } from '../../../i18n/types';

/**
 * Texts of the automation runs pages. Spread into the app dictionaries
 * (i18n/en.ts, i18n/ru.ts); keys start with `automation.runs.`.
 */
export const automationRunsEn = {
  'automation.runs.downloadReport': 'Download report',
  'automation.runs.downloadReportHint':
    'An Excel file for people outside the code: the result, every scenario, what failed, why and who should look',
  // ─── shared ───
  'automation.runs.segment.IP': 'IE',
  'automation.runs.segment.TOO': 'LLP',
  'automation.runs.byHere': 'TMS',
  'automation.runs.duration.seconds': '{s} s',
  'automation.runs.duration.minutes': '{m} min {s} s',
  'automation.runs.duration.hours': '{h} h {m} min',

  // ─── new run ───
  'automation.runs.new.title': 'New run',
  'automation.runs.new.subtitle':
    'Set up the run and start the autotests through a GitLab pipeline.',
  'automation.runs.new.noProjects': 'No projects to run yet',
  'automation.runs.new.noRunTitle': 'Running is not available.',
  'automation.runs.new.noRunNote':
    'Your role is {role}: you can view runs and data, but not start them. Starting needs the QA engineer role or higher in TMS — ask an administrator.',
  'automation.runs.new.noAccess':
    'TMS could not issue TestRun access, so your run role is unknown: {error}',
  'automation.runs.new.team': 'Team',
  'automation.runs.new.project': 'Project',
  'automation.runs.new.runType': 'Run type',
  'automation.runs.new.type.all': 'All tests',
  'automation.runs.new.type.suite': 'Suite',
  'automation.runs.new.type.classes': 'Classes',
  'automation.runs.new.pickSuite': 'Suite',
  'automation.runs.new.suitePlaceholder': '— choose a suite —',
  'automation.runs.new.suiteOption': '{name} ({count})',
  'automation.runs.new.pickClasses': 'Classes ({count})',
  'automation.runs.new.domains': 'Domains (JUnit tags) — optional',
  'automation.runs.new.domainsChosen': 'chosen {count}',
  'automation.runs.new.domainsReset': 'Reset',
  'automation.runs.new.domainsHintMany': 'Several tags →',
  'automation.runs.new.domainsHintAny': '(a class fits if it has any of them)',
  'automation.runs.new.domainsHint': 'A filter by @Tag → adds',
  'automation.runs.new.domainsHintSeveral': 'You can choose several.',
  'automation.runs.new.segment': 'IE / LLP',
  'automation.runs.new.segmentAll': 'All',
  'automation.runs.new.segmentHint': 'By',
  'automation.runs.new.segmentHintOn': 'adds',
  'automation.runs.new.segmentHintTo': 'to',
  'automation.runs.new.segmentHintOff': 'classes with either tag',
  'automation.runs.new.testType': 'Test type',
  'automation.runs.new.testType.API': 'API tests',
  'automation.runs.new.testType.UI': 'UI tests',
  'automation.runs.new.testTypeHint.API': 'REST / gateway — no browser',
  'automation.runs.new.testTypeHint.UI': 'Web / mobile — needs a browser and a device',
  'automation.runs.new.testTypeNote.API': 'No browser or device needed.',
  'automation.runs.new.testTypeNote.UI': 'Browser and device choices appear.',
  'automation.runs.new.environment': 'Environment',
  'automation.runs.new.envOnly': 'For now runs go to this environment only.',
  'automation.runs.new.browser': 'Browser',
  'automation.runs.new.device': 'Device',
  'automation.runs.new.summary': 'Summary',
  'automation.runs.new.summaryTests': 'Tests to run',
  'automation.runs.new.summaryScope': 'Scope',
  'automation.runs.new.summaryClasses': { one: '{count} class', other: '{count} classes' },
  'automation.runs.new.launch': 'Run',
  'automation.runs.new.launchChecked': 'Check data and run',
  'automation.runs.new.creating': 'Creating the pipeline…',
  'automation.runs.new.launchNote': 'The server calls the GitLab API and creates a new pipeline',
  'automation.runs.new.created': 'Run {id} created',
  'automation.runs.new.marksFailed':
    'The run started, but TestRun was not told which pool accounts it took: {error}',

  // ─── the command ───
  'automation.runs.command.title': 'Command for the GitLab Runner',
  'automation.runs.command.copy': 'Copy',
  'automation.runs.command.copied': 'Command copied',
  'automation.runs.command.fromRepo':
    'The task {task} comes from build.gradle of the connected repository. The parameters below come from the «Test data» tab.',
  'automation.runs.command.fromData':
    "The command is built from the project's test data. Connect the autotest repository so the task and the parameters come from the sources.",
  'automation.runs.command.prop': '-D parameter',
  'automation.runs.command.value': 'Value',
  'automation.runs.command.ciVar': 'CI/CD variable',
  'automation.runs.command.passing': 'Passed as',
  'automation.runs.command.ciBadge': 'CI var',
  'automation.runs.command.ciBadgeHint': 'Passed through .gitlab-ci.yml',
  'automation.runs.command.dBadgeHint': 'Passed to Gradle as -D',

  // ─── pre-flight ───
  'automation.runs.preflight.title': 'Check the run data',
  'automation.runs.preflight.noPlan': 'The class catalogue of this project is not available.',
  'automation.runs.preflight.classCount': { one: '{count} class', other: '{count} classes' },
  'automation.runs.preflight.caseCount': { one: '{count} case', other: '{count} cases' },
  'automation.runs.preflight.accountCount': { one: '{count} account', other: '{count} accounts' },
  'automation.runs.preflight.excludedCount': 'removed: {count}',
  'automation.runs.preflight.chosenClasses': 'chosen classes',
  'automation.runs.preflight.intro':
    'Check which account each class uses. Before the run you can change the account for a group or a single class.',
  'automation.runs.preflight.poolUnavailable':
    'Pool accounts are not available: {error} Accounts can still be typed by hand.',
  'automation.runs.preflight.auto.single':
    'A pool account was picked for the whole run: by the check, the given one does not fit some classes.',
  'automation.runs.preflight.auto.rotate':
    'In turn from the pool: {count} cl. (rotateAccount) — the longest-unused account, so logins, OTP and biometry do not pile up on one IIN. Marked «in turn».',
  'automation.runs.preflight.auto.reg':
    'Registration classes got an unregistered account from «For registration»: {count} cl. After the run TestRun checks whether the client was created and moves the account to the pool; if not, returns it to the list. Marked «for registration».',
  'automation.runs.preflight.auto.regKept':
    'No free accounts for registration: {list} — top up the list in «Accounts» → «Registration» or set an account by hand.',
  'automation.runs.preflight.auto.picked':
    'A pool account was picked: {count} cl. — by the check the given one does not fit their requires. Marked «picked».',
  'automation.runs.preflight.auto.fresh':
    'One-off classes got an unused pool account: {count} cl. — after the run it is marked spent for that class. Marked «new from the pool».',
  'automation.runs.preflight.auto.freshKept':
    'No free account in the pool: {list} — set an account by hand or remove the class.',
  'automation.runs.preflight.auto.keptSingle':
    'The given account stays: it does not fit every class, and the pool has none that fits all.',
  'automation.runs.preflight.auto.kept':
    'The given one stays: {count} cl. — no fitting account in the pool ({list}).',
  'automation.runs.preflight.auto.revert': 'Restore the given ones',
  'automation.runs.preflight.auto.revertHint':
    'Undo the pick and the issue: the classes run on the accounts from testdata',
  'automation.runs.preflight.missingClasses': 'The repository no longer has these classes:',
  'automation.runs.preflight.missingClassesHint':
    '— they were renamed or removed and stayed in the list from the last scan. Refresh the catalogue with «Scan repository» on the project page.',
  'automation.runs.preflight.allExcluded': 'Every class is removed from this run — nothing to run.',
  'automation.runs.preflight.nothingMatched':
    'No class matched the chosen classes and tags — nothing to run.',
  'automation.runs.preflight.overridesApply': 'Classes changed: {count}. The values go as',
  'automation.runs.preflight.overridesApplyHint':
    "the project's CaseData reads them; the other classes stay on their accounts.",
  'automation.runs.preflight.overridesIgnored':
    'but CaseData reads only the global auth.<field>: until the repository supports per-class keys the override does not apply. Run the class separately or fix the value in {file}.',
  'automation.runs.preflight.overridesSnippet':
    'Classes overridden: {count}. For a per-class override the tests must read',
  'automation.runs.preflight.showPatch': 'show the PreprodTest patch',
  'automation.runs.preflight.single':
    'The project runs on one account for the whole run (testrun.singleAccount in testdata): the account is chosen for every class at once — the tests would reduce different accounts to one, with an error.',
  'automation.runs.preflight.pickerRun': 'Pool account (whole run)',
  'automation.runs.preflight.pickerGroup': 'Pool account (group)',
  'automation.runs.preflight.pickerClass': 'Pool account (this class only)',
  'automation.runs.preflight.noSegment': 'Without an IP / TOO tag',
  'automation.runs.preflight.classesCases': '{classes} cl. / {cases} cases',
  'automation.runs.preflight.override': 'override',
  'automation.runs.preflight.groupField': '{field} (group)',
  'automation.runs.preflight.classField': '{field} (this class only)',
  'automation.runs.preflight.mixed': 'classes differ — see below',
  'automation.runs.preflight.binOnlyLlp': 'LLP scenarios only',
  'automation.runs.preflight.badge.fresh': 'new account',
  'automation.runs.preflight.badge.changed': 'changed',
  'automation.runs.preflight.badge.rotate': 'in turn',
  'automation.runs.preflight.badge.rotateHint':
    'The longest-unused pool account — logins do not pile up on one IIN',
  'automation.runs.preflight.badge.reg': 'for registration',
  'automation.runs.preflight.badge.regHint':
    'An unregistered client from «For registration»; TestRun checks the outcome after the run',
  'automation.runs.preflight.badge.freshPicked': 'new from the pool',
  'automation.runs.preflight.badge.freshPickedHint':
    'This class was never given this account; after the run it is marked spent',
  'automation.runs.preflight.badge.picked': 'picked',
  'automation.runs.preflight.badge.pickedHint':
    'By the check the given account does not fit the requires — picked from the pool',
  'automation.runs.preflight.exclude': 'Remove the class from this run',
  'automation.runs.preflight.excludeLabel': 'Remove {name} from the run',
  'automation.runs.preflight.fresh.resolved': 'The account is set for this run.',
  'automation.runs.preflight.fresh.needed': 'Needs a new account on every run.',
  'automation.runs.preflight.fresh.action':
    'Set an account in the class row or remove the class with the cross — until then the run is not available.',
  'automation.runs.preflight.fresh.destructive': 'Destructive cases: {list}.',
  'automation.runs.preflight.fresh.source': 'Source: {list}.',
  'automation.runs.preflight.freshSource.testdata': 'freshAccount in testdata',
  'automation.runs.preflight.freshSource.guard': 'a guard in the code',
  'automation.runs.preflight.freshSource.tag': '@Tag DESTRUCTIVE',
  'automation.runs.preflight.freshSource.displayName': '@DisplayName',
  'automation.runs.preflight.freshSource.comment': 'comment in testdata',
  'automation.runs.preflight.freshSource.note': 'class note',
  'automation.runs.preflight.note.label': 'Class note — everyone who runs it will see it',
  'automation.runs.preflight.note.placeholder':
    'e.g. needs a fresh account: after the run the account goes to the stop list',
  'automation.runs.preflight.note.save': 'Save note',
  'automation.runs.preflight.note.by': '{by}, {date}',
  'automation.runs.preflight.cases': 'Cases ({count})',
  'automation.runs.preflight.destructive': 'destructive',
  'automation.runs.preflight.excludedTitle':
    'Removed from this run ({count}) — they come back by themselves next time',
  'automation.runs.preflight.restoreAll': 'Restore all',
  'automation.runs.preflight.restore': 'Back into the run',
  'automation.runs.preflight.blocked': 'Needs a new account: {count}',
  'automation.runs.preflight.blockedHint':
    '— set an account for the class or remove it with the cross.',
  'automation.runs.preflight.blockedLaunch':
    'First set a new account or remove the class from the run',
  'automation.runs.preflight.command': 'Command',
  'automation.runs.preflight.problemsTitle': 'Do not fit after the recheck: {count} cl.',
  'automation.runs.preflight.problemsHint':
    'Choose another account in «Pool account» of the group or class — or run as it is.',
  'automation.runs.preflight.problemsUnknown':
    '{count} more cl. could not be checked: the stand returned no data.',
  'automation.runs.preflight.recheckFailed': 'could not recheck: {error}',
  'automation.runs.preflight.recheck': 'Recheck accounts before the run ({count} from the pool)',
  'automation.runs.preflight.recheckNothing': 'No pool accounts in the run — nothing to recheck',
  'automation.runs.preflight.recheckTitle':
    'Before the run TestRun rechecks on the stand the pool accounts the classes go under, and compares them with the requires',
  'automation.runs.preflight.creating': 'Creating…',
  'automation.runs.preflight.rechecking': 'Rechecking accounts {done}/{total}…',
  'automation.runs.preflight.launchAnyway': 'Run anyway',
  'automation.runs.preflight.launch': { one: 'Run {count} case', other: 'Run {count} cases' },

  // ─── pool picker and fit ───
  'automation.runs.picker.fits': 'Fit ({count})',
  'automation.runs.picker.unchecked': 'Not checked ({count})',
  'automation.runs.picker.unfit': 'Do not fit ({count})',
  'automation.runs.picker.noRequirements': 'No requirements ({count})',
  'automation.runs.picker.mixed':
    '— the classes have different accounts: choose one for the group —',
  'automation.runs.picker.notInPool': '— the current account is not in the pool: choose —',
  'automation.runs.picker.fitsAll': '✓ The current account fits every class of the group',
  'automation.runs.picker.fitsOne': '✓ The current account fits',
  'automation.runs.picker.doesNotFit': '✗ The current account does not fit: {why}',
  'automation.runs.verdict.ok': 'fits',
  'automation.runs.verdict.type': 'another client type',
  'automation.runs.verdict.spent': 'spent by this class ({detail})',
  'automation.runs.verdict.missing': 'no: {list}',
  'automation.runs.verdict.broken': 'the stand returned no data: {list}',
  'automation.runs.verdict.notChecked': 'could not check: {list}',
  'automation.runs.verdict.unchecked': 'account not checked',
  'automation.runs.verdict.fitOf': '{fit} of {of} cl.; {text}',
  'automation.runs.verdict.badgeUnchecked': 'not checked',

  // ─── one run ───
  'automation.runs.backToProject': 'To the project',
  'automation.runs.runTitle': 'Run {id}',
  'automation.runs.rerunFailed': 'Rerun failed',
  'automation.runs.rerunFailedHint': 'Start again the classes that have failed tests',
  'automation.runs.rerunStarted': 'Rerun of the failed classes started',
  'automation.runs.cancel': 'Cancel run',
  'automation.runs.cancelled': 'Run cancelled',
  'automation.runs.cancelTitle': 'Cancel run {id}?',
  'automation.runs.cancelDescription':
    'GitLab stops the pipeline: the tests that have not run yet will not run. What has finished stays in the report.',
  'automation.runs.keepGoing': 'Keep running',
  'automation.runs.allure': 'Allure Report',
  'automation.runs.tests': 'Tests',
  'automation.runs.testsHint': 'Results from JUnit XML',
  'automation.runs.report': 'Report',
  'automation.runs.reportHint': 'Gradle HTML report from the artifacts',
  'automation.runs.pipeline': 'Pipeline',
  'automation.runs.meta.pipeline': 'Pipeline',
  'automation.runs.meta.runner': 'Runner',
  'automation.runs.meta.environment': 'Environment',
  'automation.runs.meta.testType': 'Type',
  'automation.runs.meta.browser': 'Browser',
  'automation.runs.meta.branch': 'Branch',
  'automation.runs.meta.started': 'Started',
  'automation.runs.meta.threads': 'Threads',
  'automation.runs.meta.command': 'Runner command',
  'automation.runs.progress': 'Progress',
  'automation.runs.stages': 'Stages',
  'automation.runs.log': 'Execution log',
  'automation.runs.live': 'live',
  'automation.runs.autoscroll': 'Autoscroll',
  'automation.runs.waitingLog': 'Waiting for the log…',
  'automation.runs.noReport.error': 'The test report did not load: {error}',
  'automation.runs.noReport.loading': 'Loading the test report from GitLab…',
  'automation.runs.noReport.empty':
    "GitLab's test report has no tests. Usually the build failed before the tests, or the report is still being built — the reason is in the log below.",

  // ─── results ───
  'automation.runs.case.failed': 'Failed',
  'automation.runs.case.broken': 'Error',
  'automation.runs.case.skipped': 'Skipped',
  'automation.runs.case.passed': 'Passed',
  'automation.runs.results.title': 'Result',
  'automation.runs.results.passedOf': 'passed {passed} of {total}',
  'automation.runs.results.executed': ', executed {count}',
  'automation.runs.results.preliminary': 'preliminary, the run is still going',
  'automation.runs.results.testTime': 'test time {duration}',
  'automation.runs.results.passed': 'Passed',
  'automation.runs.results.failed': 'Failed',
  'automation.runs.results.broken': 'Errors',
  'automation.runs.results.brokenHint': 'An exception outside the check (broken in Allure)',
  'automation.runs.results.skipped': 'Skipped',
  'automation.runs.results.total': 'Total',
  'automation.runs.results.suiteError':
    'GitLab could not parse the report of suite «{name}»: {error}',
  'automation.runs.compare.pick': 'choose a run…',
  'automation.runs.compare.pickLabel': 'Run to compare with',
  'automation.runs.compare.title': 'Comparison with an earlier run',
  'automation.runs.compare.titleWith': 'Comparison with run {run}',
  'automation.runs.compare.nothing':
    "Nothing to compare with: none of the project's latest finished runs has the same tests.",
  'automation.runs.compare.newFailures': 'Failed for the first time',
  'automation.runs.compare.fixed': 'Fixed',
  'automation.runs.compare.stillFailing': 'Red as before',
  'automation.runs.compare.newlySkipped': 'Now skipped',
  'automation.runs.compare.newFailures.empty': 'No new failures.',
  'automation.runs.compare.fixed.empty': 'Nothing got fixed.',
  'automation.runs.compare.stillFailing.empty': 'Nothing red from the earlier run.',
  'automation.runs.compare.newlySkipped.empty': 'No new skips.',
  'automation.runs.compare.common': 'Common tests: {count}.',
  'automation.runs.compare.notInPrevious': '{run} did not have {count}.',
  'automation.runs.compare.notInCurrent': '{count} of {run} did not run now.',
  'automation.runs.compare.wasAbsent': 'this test was not in the earlier run',
  'automation.runs.compare.wasSkipped': 'was skipped in the earlier run',
  'automation.runs.compare.wasStatus': 'in the earlier run: {status}',
  'automation.runs.compare.failedWith': 'failed with:',
  'automation.runs.compare.failedOtherwise': 'failed differently before:',
  'automation.runs.classes.title': 'By class',
  'automation.runs.classes.red': 'With failures {count}',
  'automation.runs.classes.skipped': 'With skips {count}',
  'automation.runs.classes.all': 'All {count}',
  'automation.runs.classes.none': 'No such classes.',
  'automation.runs.classes.expand': 'Open',
  'automation.runs.classes.class': 'Class',
  'automation.runs.classes.time': 'Time',
  'automation.runs.classes.toggle': 'Tests of {name}',
  'automation.runs.classes.brokenOf': 'of them errors outside the check',
  'automation.runs.classes.showOutput': 'Stack and output',
  'automation.runs.classes.hideOutput': 'Hide the stack',

  // ─── full run ───
  'automation.runs.full.title': 'Full run',
  'automation.runs.full.subtitle':
    'Every project in one run, and a summary: how many failed and on whose side — our backend, Colvir, the stand, the data or the run itself. Known failures carry their number from the QA defect register.',
  'automation.runs.full.start': 'Run all projects ({env})',
  'automation.runs.full.confirmTitle': 'Run all projects on {env}?',
  'automation.runs.full.confirmDescription':
    'Each project is a separate pipeline with the accounts from its own test data. Classes that need their own pool account skip themselves without one. Logins go in parallel — OTP limits are possible.',
  'automation.runs.full.confirm': 'Run all',
  'automation.runs.full.started': {
    one: '{count} project started',
    other: '{count} projects started',
  },
  'automation.runs.full.startedPartly':
    'Started {started} of {total} projects — the rest did not start',
  'automation.runs.full.empty': 'No full runs yet',
  'automation.runs.full.emptyHint': 'The button at the top runs every project on {env}.',
  'automation.runs.full.list': 'Full runs',
  'automation.runs.full.notStartedCount': 'not started: {count}',
  'automation.runs.full.by': 'Started by {by} · {when} · {env}',
  'automation.runs.full.going':
    'projects still going: {count} of {total} — the summary refreshes every 15 s',
  'automation.runs.full.tests': 'Tests',
  'automation.runs.full.whoseSide': 'Whose side',
  'automation.runs.full.byProject': 'By project',
  'automation.runs.full.pipeline': 'Pipeline',
  'automation.runs.full.failuresBySource': 'Failures by source',
  'automation.runs.full.notStarted': 'not started',
  'automation.runs.full.noPipeline': 'the pipeline was not created',
  'automation.runs.full.noReportFinal': 'no test report — the pipeline failed before the tests',
  'automation.runs.full.noReportYet': 'no report yet — the tests are running',
  'automation.runs.full.totals': 'Total',
  'automation.runs.full.byCause': 'Failures by cause',
  'automation.runs.full.unrecognised':
    'Not recognised: {count}. Sort them out by the error text; if it is a defect, add it to the register and its signs to the known causes — the next run labels it with its number.',
  'automation.runs.full.more': "and {count} more — on the project's run page",
  'automation.runs.full.howSources':
    'How the source is found: a red test is compared with the known causes (register number, source, signs by class, test name and error text). Not recognised — by the error text: the stand and login — «Stand», an autotest error — «Our run», the rest — «New». A failure in a class where login already failed or the stand did not answer counts as its consequence («cascade»).',
  'automation.runs.full.knownDefects': 'Known causes ({count})',
  'automation.runs.full.knownDefectsHint':
    'Entries of the QA defect register the summary recognises: a failure that matches one carries its number.',
  'automation.runs.full.defect': 'Number',
  'automation.runs.full.defectTitle': 'Defect',
  'automation.runs.full.source': 'Source',
} as const;

export const automationRunsRu: Record<keyof typeof automationRunsEn, Message> = {
  'automation.runs.downloadReport': 'Скачать отчёт',
  'automation.runs.downloadReportHint':
    'Excel для тех, кто не в коде: итог, каждый сценарий, что упало, почему и кто разбирается',
  // ─── shared ───
  'automation.runs.segment.IP': 'ИП',
  'automation.runs.segment.TOO': 'ТОО',
  'automation.runs.byHere': 'TMS',
  'automation.runs.duration.seconds': '{s} с',
  'automation.runs.duration.minutes': '{m} мин {s} с',
  'automation.runs.duration.hours': '{h} ч {m} мин',

  // ─── new run ───
  'automation.runs.new.title': 'Новый запуск',
  'automation.runs.new.subtitle':
    'Настройте параметры и запустите автотесты через GitLab pipeline.',
  'automation.runs.new.noProjects': 'Запускать пока нечего — проектов нет',
  'automation.runs.new.noRunTitle': 'Запуск недоступен.',
  'automation.runs.new.noRunNote':
    'У вас роль {role}: можно смотреть запуски и данные, но не запускать. Запуск доступен с роли QA-инженер в TMS — попросите администратора.',
  'automation.runs.new.noAccess':
    'TMS не выдал доступ к TestRun, поэтому роль для запуска неизвестна: {error}',
  'automation.runs.new.team': 'Команда',
  'automation.runs.new.project': 'Проект',
  'automation.runs.new.runType': 'Тип запуска',
  'automation.runs.new.type.all': 'Все тесты',
  'automation.runs.new.type.suite': 'Suite',
  'automation.runs.new.type.classes': 'Классы',
  'automation.runs.new.pickSuite': 'Выбор suite',
  'automation.runs.new.suitePlaceholder': '— выберите suite —',
  'automation.runs.new.suiteOption': '{name} ({count})',
  'automation.runs.new.pickClasses': 'Выбор классов ({count})',
  'automation.runs.new.domains': 'Домены (JUnit теги) — опционально',
  'automation.runs.new.domainsChosen': 'выбрано {count}',
  'automation.runs.new.domainsReset': 'Сбросить',
  'automation.runs.new.domainsHintMany': 'Несколько тегов →',
  'automation.runs.new.domainsHintAny': '(класс подойдёт, если у него есть любой из них)',
  'automation.runs.new.domainsHint': 'Фильтр по @Tag → добавит',
  'automation.runs.new.domainsHintSeveral': 'Можно выбрать несколько.',
  'automation.runs.new.segment': 'ИП / ТОО',
  'automation.runs.new.segmentAll': 'Все',
  'automation.runs.new.segmentHint': 'По',
  'automation.runs.new.segmentHintOn': 'добавит',
  'automation.runs.new.segmentHintTo': 'в',
  'automation.runs.new.segmentHintOff': 'классы с любым из тегов',
  'automation.runs.new.testType': 'Тип тестов',
  'automation.runs.new.testType.API': 'API тесты',
  'automation.runs.new.testType.UI': 'UI тесты',
  'automation.runs.new.testTypeHint.API': 'REST / gateway — без браузера',
  'automation.runs.new.testTypeHint.UI': 'Web / mobile — нужен браузер и девайс',
  'automation.runs.new.testTypeNote.API': 'Браузер и девайс не требуются.',
  'automation.runs.new.testTypeNote.UI': 'Откроются выбор браузера и устройства.',
  'automation.runs.new.environment': 'Окружение',
  'automation.runs.new.envOnly': 'Пока запускаем только на этой среде.',
  'automation.runs.new.browser': 'Браузер',
  'automation.runs.new.device': 'Устройство',
  'automation.runs.new.summary': 'Сводка',
  'automation.runs.new.summaryTests': 'Тестов к запуску',
  'automation.runs.new.summaryScope': 'Что запускаем',
  'automation.runs.new.summaryClasses': {
    one: '{count} класс',
    few: '{count} класса',
    many: '{count} классов',
    other: '{count} класса',
  },
  'automation.runs.new.launch': 'Запустить',
  'automation.runs.new.launchChecked': 'Проверить данные и запустить',
  'automation.runs.new.creating': 'Создание pipeline…',
  'automation.runs.new.launchNote': 'Сервер вызовет GitLab API и создаст новый pipeline',
  'automation.runs.new.created': 'Запуск {id} создан',
  'automation.runs.new.marksFailed':
    'Запуск начат, но TestRun не узнал, какие аккаунты пула он взял: {error}',

  // ─── the command ───
  'automation.runs.command.title': 'Команда для GitLab Runner',
  'automation.runs.command.copy': 'Копировать',
  'automation.runs.command.copied': 'Команда скопирована',
  'automation.runs.command.fromRepo':
    'Задача {task} определена из build.gradle подключённого репозитория. Параметры ниже собраны из вкладки «Тестовые данные».',
  'automation.runs.command.fromData':
    'Команда собрана из тестовых данных проекта. Подключите репозиторий автотестов, чтобы задача и параметры подтягивались из исходников.',
  'automation.runs.command.prop': '-D параметр',
  'automation.runs.command.value': 'Значение',
  'automation.runs.command.ciVar': 'CI/CD variable',
  'automation.runs.command.passing': 'Передача',
  'automation.runs.command.ciBadge': 'CI var',
  'automation.runs.command.ciBadgeHint': 'Проброшено через .gitlab-ci.yml',
  'automation.runs.command.dBadgeHint': 'Передаётся как -D в gradle',

  // ─── pre-flight ───
  'automation.runs.preflight.title': 'Проверка данных запуска',
  'automation.runs.preflight.noPlan': 'Для этого проекта каталог классов недоступен.',
  'automation.runs.preflight.classCount': {
    one: '{count} класс',
    few: '{count} класса',
    many: '{count} классов',
    other: '{count} класса',
  },
  'automation.runs.preflight.caseCount': {
    one: '{count} кейс',
    few: '{count} кейса',
    many: '{count} кейсов',
    other: '{count} кейса',
  },
  'automation.runs.preflight.accountCount': {
    one: '{count} аккаунт',
    few: '{count} аккаунта',
    many: '{count} аккаунтов',
    other: '{count} аккаунта',
  },
  'automation.runs.preflight.excludedCount': 'убрано: {count}',
  'automation.runs.preflight.chosenClasses': 'выбранные классы',
  'automation.runs.preflight.intro':
    'Проверьте, какой аккаунт используется в каждом классе. Можно изменить аккаунт для группы или отдельного класса перед запуском.',
  'automation.runs.preflight.poolUnavailable':
    'Аккаунты пула недоступны: {error} Аккаунт можно ввести вручную.',
  'automation.runs.preflight.auto.single':
    'Подобран аккаунт из пула на весь прогон: указанный по проверке не подходит части классов.',
  'automation.runs.preflight.auto.rotate':
    'По очереди из пула: {count} кл. (rotateAccount) — давно не использованный аккаунт, чтобы входы, OTP и биометрия не копились на одном ИИН. Метка «по очереди».',
  'automation.runs.preflight.auto.reg':
    'Классам регистрации выдан незарегистрированный аккаунт из списка «На регистрацию»: {count} кл. После прогона TestRun проверит, создан ли клиент, и уберёт аккаунт в пул; если нет — вернёт в список. Метка «на регистрацию».',
  'automation.runs.preflight.auto.regKept':
    'Свободных аккаунтов на регистрацию нет: {list} — пополните список в «Аккаунты» → «Регистрация» или задайте аккаунт вручную.',
  'automation.runs.preflight.auto.picked':
    'Подобран аккаунт из пула: {count} кл. — указанный по проверке не подходит по requires. Метка «подобран».',
  'automation.runs.preflight.auto.fresh':
    'Одноразовым классам выдан неиспользованный аккаунт из пула: {count} кл. — после запуска он будет помечен израсходованным для этого класса. Метка «новый из пула».',
  'automation.runs.preflight.auto.freshKept':
    'Свободного аккаунта в пуле нет: {list} — задайте аккаунт вручную или уберите класс.',
  'automation.runs.preflight.auto.keptSingle':
    'Оставлен указанный аккаунт: он подходит не всем классам, а подходящего всем в пуле нет.',
  'automation.runs.preflight.auto.kept':
    'Оставлен указанный: {count} кл. — подходящего аккаунта в пуле нет ({list}).',
  'automation.runs.preflight.auto.revert': 'Вернуть указанные',
  'automation.runs.preflight.auto.revertHint':
    'Отменить подбор и выдачу: классы пойдут на аккаунтах из testdata',
  'automation.runs.preflight.missingClasses': 'В репозитории больше нет таких классов:',
  'automation.runs.preflight.missingClassesHint':
    '— их переименовали или удалили, а в списке они остались от прошлого сканирования. Обновите каталог кнопкой «Просканировать репозиторий» на странице проекта.',
  'automation.runs.preflight.allExcluded': 'Все классы убраны из этого запуска — запускать нечего.',
  'automation.runs.preflight.nothingMatched':
    'Под выбранные классы и теги не подошёл ни один класс — запускать нечего.',
  'automation.runs.preflight.overridesApply': 'Изменено классов: {count}. Значения уйдут как',
  'automation.runs.preflight.overridesApplyHint':
    'CaseData проекта их читает, остальные классы останутся на своих аккаунтах.',
  'automation.runs.preflight.overridesIgnored':
    'но CaseData читает только глобальный auth.<поле>: пока в репозитории нет поддержки покласных ключей, подмена не применится. Запустите класс отдельно или поправьте значение в {file}.',
  'automation.runs.preflight.overridesSnippet':
    'Переопределено классов: {count}. Для покласной подмены тесты должны читать',
  'automation.runs.preflight.showPatch': 'показать патч PreprodTest',
  'automation.runs.preflight.single':
    'Проект идёт одним аккаунтом на весь прогон (testrun.singleAccount в testdata): аккаунт выбирается сразу на все классы — разные аккаунты тесты свели бы к одному с ошибкой.',
  'automation.runs.preflight.pickerRun': 'Аккаунт из пула (на весь прогон)',
  'automation.runs.preflight.pickerGroup': 'Аккаунт из пула (на группу)',
  'automation.runs.preflight.pickerClass': 'Аккаунт из пула (только этот класс)',
  'automation.runs.preflight.noSegment': 'Без тега IP / TOO',
  'automation.runs.preflight.classesCases': '{classes} кл. / {cases} кейс.',
  'automation.runs.preflight.override': 'override',
  'automation.runs.preflight.groupField': '{field} (на группу)',
  'automation.runs.preflight.classField': '{field} (только этот класс)',
  'automation.runs.preflight.mixed': 'у классов разные — см. ниже',
  'automation.runs.preflight.binOnlyLlp': 'только для ТОО-сценариев',
  'automation.runs.preflight.badge.fresh': 'новый аккаунт',
  'automation.runs.preflight.badge.changed': 'изменён',
  'automation.runs.preflight.badge.rotate': 'по очереди',
  'automation.runs.preflight.badge.rotateHint':
    'Давно не использованный аккаунт пула — входы не копятся на одном ИИН',
  'automation.runs.preflight.badge.reg': 'на регистрацию',
  'automation.runs.preflight.badge.regHint':
    'Незарегистрированный клиент из списка «На регистрацию»; после прогона TestRun проверит итог',
  'automation.runs.preflight.badge.freshPicked': 'новый из пула',
  'automation.runs.preflight.badge.freshPickedHint':
    'Этому классу аккаунт ещё не выдавали; после запуска пометится израсходованным',
  'automation.runs.preflight.badge.picked': 'подобран',
  'automation.runs.preflight.badge.pickedHint':
    'Указанный аккаунт по проверке не подходит по requires — подобран из пула',
  'automation.runs.preflight.exclude': 'Убрать класс из этого запуска',
  'automation.runs.preflight.excludeLabel': 'Убрать {name} из запуска',
  'automation.runs.preflight.fresh.resolved': 'Аккаунт задан для этого запуска.',
  'automation.runs.preflight.fresh.needed': 'Нужен новый аккаунт на каждый прогон.',
  'automation.runs.preflight.fresh.action':
    'Укажите аккаунт в строке класса или уберите класс крестиком — до этого запуск недоступен.',
  'automation.runs.preflight.fresh.destructive': 'Разрушающие кейсы: {list}.',
  'automation.runs.preflight.fresh.source': 'Источник: {list}.',
  'automation.runs.preflight.freshSource.testdata': 'freshAccount в testdata',
  'automation.runs.preflight.freshSource.guard': 'предохранитель в коде',
  'automation.runs.preflight.freshSource.tag': '@Tag DESTRUCTIVE',
  'automation.runs.preflight.freshSource.displayName': '@DisplayName',
  'automation.runs.preflight.freshSource.comment': 'comment в testdata',
  'automation.runs.preflight.freshSource.note': 'примечание',
  'automation.runs.preflight.note.label':
    'Примечание к классу — увидят все, кто будет его запускать',
  'automation.runs.preflight.note.placeholder':
    'напр. нужен свежий аккаунт: после прогона счёт попадает в стоп-лист',
  'automation.runs.preflight.note.save': 'Сохранить примечание',
  'automation.runs.preflight.note.by': '{by}, {date}',
  'automation.runs.preflight.cases': 'Кейсы ({count})',
  'automation.runs.preflight.destructive': 'разрушающий',
  'automation.runs.preflight.excludedTitle':
    'Убраны из этого запуска ({count}) — в следующий запуск вернутся сами',
  'automation.runs.preflight.restoreAll': 'Вернуть все',
  'automation.runs.preflight.restore': 'Вернуть в запуск',
  'automation.runs.preflight.blocked': 'Нужен новый аккаунт: {count}',
  'automation.runs.preflight.blockedHint': '— задайте аккаунт классу или уберите его крестиком.',
  'automation.runs.preflight.blockedLaunch':
    'Сначала задайте новый аккаунт или уберите класс из запуска',
  'automation.runs.preflight.command': 'Команда',
  'automation.runs.preflight.problemsTitle': 'После перепроверки не подходят: {count} кл.',
  'automation.runs.preflight.problemsHint':
    'Выберите другой аккаунт в поле «Аккаунт из пула» у группы или класса — или запустите как есть.',
  'automation.runs.preflight.problemsUnknown':
    'Ещё {count} кл. проверить не удалось: стенд не отдал данные.',
  'automation.runs.preflight.recheckFailed': 'не удалось перепроверить: {error}',
  'automation.runs.preflight.recheck': 'Перепроверить аккаунты перед запуском ({count} из пула)',
  'automation.runs.preflight.recheckNothing':
    'Аккаунтов из пула в запуске нет — перепроверять нечего',
  'automation.runs.preflight.recheckTitle':
    'Перед запуском TestRun заново проверит на стенде аккаунты из пула, под которыми пойдут классы, и сверит с requires',
  'automation.runs.preflight.creating': 'Создание…',
  'automation.runs.preflight.rechecking': 'Перепроверяем аккаунты {done}/{total}…',
  'automation.runs.preflight.launchAnyway': 'Запустить всё равно',
  'automation.runs.preflight.launch': {
    one: 'Запустить {count} кейс',
    few: 'Запустить {count} кейса',
    many: 'Запустить {count} кейсов',
    other: 'Запустить {count} кейса',
  },

  // ─── pool picker and fit ───
  'automation.runs.picker.fits': 'Подходят ({count})',
  'automation.runs.picker.unchecked': 'Не проверены ({count})',
  'automation.runs.picker.unfit': 'Не подходят ({count})',
  'automation.runs.picker.noRequirements': 'Требований нет ({count})',
  'automation.runs.picker.mixed': '— у классов разные аккаунты: выберите один на всю группу —',
  'automation.runs.picker.notInPool': '— текущего аккаунта нет в пуле: выберите —',
  'automation.runs.picker.fitsAll': '✓ Текущий аккаунт подходит всем классам группы',
  'automation.runs.picker.fitsOne': '✓ Текущий аккаунт подходит',
  'automation.runs.picker.doesNotFit': '✗ Текущий аккаунт не подходит: {why}',
  'automation.runs.verdict.ok': 'подходит',
  'automation.runs.verdict.type': 'другой тип клиента',
  'automation.runs.verdict.spent': 'израсходован этим классом ({detail})',
  'automation.runs.verdict.missing': 'нет: {list}',
  'automation.runs.verdict.broken': 'стенд не отдал данные: {list}',
  'automation.runs.verdict.notChecked': 'не удалось проверить: {list}',
  'automation.runs.verdict.unchecked': 'аккаунт не проверен',
  'automation.runs.verdict.fitOf': '{fit} из {of} кл.; {text}',
  'automation.runs.verdict.badgeUnchecked': 'не проверен',

  // ─── one run ───
  'automation.runs.backToProject': 'К проекту',
  'automation.runs.runTitle': 'Запуск {id}',
  'automation.runs.rerunFailed': 'Перезапустить упавшие',
  'automation.runs.rerunFailedHint': 'Запустить заново классы, в которых есть упавшие тесты',
  'automation.runs.rerunStarted': 'Перезапуск упавших классов начат',
  'automation.runs.cancel': 'Отменить запуск',
  'automation.runs.cancelled': 'Запуск отменён',
  'automation.runs.cancelTitle': 'Отменить запуск {id}?',
  'automation.runs.cancelDescription':
    'GitLab остановит pipeline: тесты, которые ещё не прошли, не запустятся. Завершённое останется в отчёте.',
  'automation.runs.keepGoing': 'Не отменять',
  'automation.runs.allure': 'Allure Report',
  'automation.runs.tests': 'Тесты',
  'automation.runs.testsHint': 'Результаты из JUnit XML',
  'automation.runs.report': 'Отчёт',
  'automation.runs.reportHint': 'HTML-отчёт Gradle из артефактов',
  'automation.runs.pipeline': 'Pipeline',
  'automation.runs.meta.pipeline': 'Pipeline',
  'automation.runs.meta.runner': 'Runner',
  'automation.runs.meta.environment': 'Окружение',
  'automation.runs.meta.testType': 'Тип',
  'automation.runs.meta.browser': 'Браузер',
  'automation.runs.meta.branch': 'Ветка',
  'automation.runs.meta.started': 'Старт',
  'automation.runs.meta.threads': 'Потоки',
  'automation.runs.meta.command': 'Команда Runner',
  'automation.runs.progress': 'Прогресс',
  'automation.runs.stages': 'Этапы',
  'automation.runs.log': 'Логи выполнения',
  'automation.runs.live': 'live',
  'automation.runs.autoscroll': 'Автопрокрутка',
  'automation.runs.waitingLog': 'Ожидание логов…',
  'automation.runs.noReport.error': 'Отчёт о тестах не загрузился: {error}',
  'automation.runs.noReport.loading': 'Загружаем отчёт о тестах из GitLab…',
  'automation.runs.noReport.empty':
    'В отчёте о тестах GitLab нет ни одного теста. Обычно это значит, что сборка упала до запуска тестов или отчёт ещё собирается — причина в логах ниже.',

  // ─── results ───
  'automation.runs.case.failed': 'Упал',
  'automation.runs.case.broken': 'Ошибка',
  'automation.runs.case.skipped': 'Пропущен',
  'automation.runs.case.passed': 'Прошёл',
  'automation.runs.results.title': 'Результат',
  'automation.runs.results.passedOf': 'прошло {passed} из {total}',
  'automation.runs.results.executed': ', выполнено {count}',
  'automation.runs.results.preliminary': 'предварительно, запуск ещё идёт',
  'automation.runs.results.testTime': 'время тестов {duration}',
  'automation.runs.results.passed': 'Прошло',
  'automation.runs.results.failed': 'Упало',
  'automation.runs.results.broken': 'Ошибки',
  'automation.runs.results.brokenHint': 'Исключение вне проверки (в Allure — broken)',
  'automation.runs.results.skipped': 'Пропущено',
  'automation.runs.results.total': 'Всего',
  'automation.runs.results.suiteError': 'GitLab не разобрал отчёт набора «{name}»: {error}',
  'automation.runs.compare.pick': 'выбрать запуск…',
  'automation.runs.compare.pickLabel': 'С каким запуском сравнивать',
  'automation.runs.compare.title': 'Сравнение с прошлым запуском',
  'automation.runs.compare.titleWith': 'Сравнение с запуском {run}',
  'automation.runs.compare.nothing':
    'Не с чем сравнить: среди последних завершённых запусков проекта нет запуска с теми же тестами.',
  'automation.runs.compare.newFailures': 'Упали впервые',
  'automation.runs.compare.fixed': 'Починились',
  'automation.runs.compare.stillFailing': 'Красные как были',
  'automation.runs.compare.newlySkipped': 'Стали пропускаться',
  'automation.runs.compare.newFailures.empty': 'Новых падений нет.',
  'automation.runs.compare.fixed.empty': 'Ничего не починилось.',
  'automation.runs.compare.stillFailing.empty': 'Красных с прошлого запуска нет.',
  'automation.runs.compare.newlySkipped.empty': 'Новых пропусков нет.',
  'automation.runs.compare.common': 'Общих тестов: {count}.',
  'automation.runs.compare.notInPrevious': 'В {run} не было {count}.',
  'automation.runs.compare.notInCurrent': 'Сейчас не запускались {count} из {run}.',
  'automation.runs.compare.wasAbsent': 'в прошлом запуске этого теста не было',
  'automation.runs.compare.wasSkipped': 'в прошлом запуске был пропущен',
  'automation.runs.compare.wasStatus': 'в прошлом запуске: {status}',
  'automation.runs.compare.failedWith': 'падал с ошибкой:',
  'automation.runs.compare.failedOtherwise': 'раньше падал иначе:',
  'automation.runs.classes.title': 'По классам',
  'automation.runs.classes.red': 'С падениями {count}',
  'automation.runs.classes.skipped': 'С пропусками {count}',
  'automation.runs.classes.all': 'Все {count}',
  'automation.runs.classes.none': 'Таких классов нет.',
  'automation.runs.classes.expand': 'Раскрыть',
  'automation.runs.classes.class': 'Класс',
  'automation.runs.classes.time': 'Время',
  'automation.runs.classes.toggle': 'Тесты класса {name}',
  'automation.runs.classes.brokenOf': 'из них ошибок вне проверки',
  'automation.runs.classes.showOutput': 'Стек и вывод',
  'automation.runs.classes.hideOutput': 'Скрыть стек',

  // ─── full run ───
  'automation.runs.full.title': 'Полный прогон',
  'automation.runs.full.subtitle':
    'Все проекты одним запуском и сводка: сколько упало и на чьей стороне — наш бэк, Colvir, стенд, данные или сам запуск. Известные падения подписаны номером из реестра дефектов QA.',
  'automation.runs.full.start': 'Запустить все проекты ({env})',
  'automation.runs.full.confirmTitle': 'Запустить все проекты на {env}?',
  'automation.runs.full.confirmDescription':
    'Каждый проект — отдельный пайплайн с аккаунтами из своих тестовых данных. Классы, которым нужен свой аккаунт пула, без него пропускаются. Входы идут параллельно — возможны лимиты OTP.',
  'automation.runs.full.confirm': 'Запустить все',
  'automation.runs.full.started': {
    one: 'Запущен {count} проект',
    few: 'Запущено {count} проекта',
    many: 'Запущено {count} проектов',
    other: 'Запущено {count} проекта',
  },
  'automation.runs.full.startedPartly':
    'Запущено {started} из {total} проектов — остальные не стартовали',
  'automation.runs.full.empty': 'Полных прогонов ещё не было',
  'automation.runs.full.emptyHint': 'Кнопка вверху запускает все проекты на {env}.',
  'automation.runs.full.list': 'Полные прогоны',
  'automation.runs.full.notStartedCount': 'не стартовало: {count}',
  'automation.runs.full.by': 'Запустил {by} · {when} · {env}',
  'automation.runs.full.going': 'идёт проектов: {count} из {total} — сводка обновляется раз в 15 с',
  'automation.runs.full.tests': 'Тестов',
  'automation.runs.full.whoseSide': 'Чья сторона',
  'automation.runs.full.byProject': 'По проектам',
  'automation.runs.full.pipeline': 'Пайплайн',
  'automation.runs.full.failuresBySource': 'Падения по источникам',
  'automation.runs.full.notStarted': 'не запущен',
  'automation.runs.full.noPipeline': 'пайплайн не создан',
  'automation.runs.full.noReportFinal': 'отчёта о тестах нет — пайплайн упал до тестов',
  'automation.runs.full.noReportYet': 'отчёта ещё нет — тесты идут',
  'automation.runs.full.totals': 'Итого',
  'automation.runs.full.byCause': 'Падения по причинам',
  'automation.runs.full.unrecognised':
    'Не узнано: {count}. Разобрать по тексту ошибки; если это дефект — завести в реестр и добавить признаки в известные причины, тогда следующий прогон подпишет его номером.',
  'automation.runs.full.more': 'и ещё {count} — на странице запуска проекта',
  'automation.runs.full.howSources':
    'Как определяется источник: красный тест сверяется с известными причинами (номер реестра, источник, признаки по классу, имени теста и тексту ошибки). Не узнан — по тексту ошибки: стенд и вход — «Стенд», ошибка автотеста — «Наш запуск», остальное — «Новое». Падение в классе, где уже не прошёл вход или не ответил стенд, считается его следствием («каскад»).',
  'automation.runs.full.knownDefects': 'Известные причины ({count})',
  'automation.runs.full.knownDefectsHint':
    'Записи реестра дефектов QA, которые узнаёт сводка: совпавшее падение подписывается их номером.',
  'automation.runs.full.defect': 'Номер',
  'automation.runs.full.defectTitle': 'Дефект',
  'automation.runs.full.source': 'Источник',
};
